import React, { useState, useEffect } from 'react';
import { useAutosave } from '../../../hooks/useAutosave';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Save, Plus, Trash2, Star, CheckCircle, Send, Mail, Clock, X, Copy, FileSignature, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ElectronicConsentForm = ({ caseId, agent, onUpdate }) => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  useAutosave(() => save(), data);

  useEffect(() => {
    fetch(`${API_URL}/api/compliance/cases/${caseId}/electronic-consent`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const base = d || {};
        setData({
          client_consents_to_electronic_delivery: true,
          client_consents_to_email: true,
          client_consents_to_sms: true,
          ...base,
        });
      })
      .catch(() => {});
  }, [caseId]);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/compliance/cases/${caseId}/electronic-consent?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      onUpdate && onUpdate();
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="electronic-consent-card">
      <CardHeader className="flex flex-row items-center justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#0077B6]" data-testid="save-ec-btn">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600">
          The client consents to receive documents, disclosures, and policy-servicing communications electronically,
          in place of paper delivery. This consent may be withdrawn at any time.
        </p>
        <div>
          <label className="block text-sm font-medium mb-1">Client Email (confirmed)</label>
          <input type="email" value={data.client_email_confirmed || ''} onChange={(e) => setData({ ...data, client_email_confirmed: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="ec-email" />
        </div>
        <div className="flex flex-col gap-2 pt-2 border-t">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.client_consents_to_electronic_delivery || false} onChange={(e) => setData({ ...data, client_consents_to_electronic_delivery: e.target.checked })} data-testid="ec-electronic" />
            <span className="text-sm">Client consents to electronic delivery of documents</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.client_consents_to_email || false} onChange={(e) => setData({ ...data, client_consents_to_email: e.target.checked })} data-testid="ec-email-ok" />
            <span className="text-sm">Client consents to email communications</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={data.client_consents_to_sms || false} onChange={(e) => setData({ ...data, client_consents_to_sms: e.target.checked })} data-testid="ec-sms" />
            <span className="text-sm">Client consents to SMS/text communications</span>
          </label>
          <label className="flex items-center gap-2 pt-2 border-t mt-2">
            <input type="checkbox" checked={data.completed || false} onChange={(e) => setData({ ...data, completed: e.target.checked })} data-testid="ec-completed" />
            <span className="text-sm font-semibold">Mark as Complete</span>
          </label>
        </div>
        <textarea rows="2" value={data.notes || ''} onChange={(e) => setData({ ...data, notes: e.target.value })} placeholder="Optional notes..." className="w-full px-3 py-2 border rounded-lg" data-testid="ec-notes" />
      </CardContent>
    </Card>
  );
};


// =============================================================
// Conflict of Interest
// =============================================================
